import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { Autom2PlataformaApi } from './Autom2PlataformaApi';
import {
  CapabilitiesDto,
  DeleteInfraLaunchDto,
  InfraWorkflowDetailDto,
  InfraWorkflowDto,
  PollerStatusDto,
} from './types';

/** Error carrying the HTTP status, so the UI can tell 403 from a real failure. */
export class Autom2PlataformaRequestError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'Autom2PlataformaRequestError';
    Object.setPrototypeOf(this, Autom2PlataformaRequestError.prototype);
  }

  /** True when the caller is authenticated but not allowed to read this. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * `fetchApi` adds the Backstage token and `discoveryApi` resolves the plugin's
 * base URL, replacing the hand-rolled `backend.baseUrl` + `identityApi` +
 * `fetch` combination used elsewhere in the app.
 */
export class Autom2PlataformaClient implements Autom2PlataformaApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('autom2plataforma');
  }

  private async getJson<T>(path: string): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!response.ok) {
      throw new Autom2PlataformaRequestError(
        response.status,
        `La petición a autom2plataforma falló (HTTP ${response.status})`,
      );
    }
    return (await response.json()) as T;
  }

  private async postJson<T>(path: string, body: unknown): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Autom2PlataformaRequestError(
        response.status,
        `La petición a autom2plataforma falló (HTTP ${response.status})`,
      );
    }
    return (await response.json()) as T;
  }

  async listWorkflowsByEntity(entityRef: string): Promise<InfraWorkflowDto[]> {
    const data = await this.getJson<{ items?: InfraWorkflowDto[] }>(
      `/workflows?entityRef=${encodeURIComponent(entityRef)}`,
    );
    return Array.isArray(data.items) ? data.items : [];
  }

  async getWorkflow(workflowId: string): Promise<InfraWorkflowDetailDto> {
    return this.getJson<InfraWorkflowDetailDto>(
      `/workflows/${encodeURIComponent(workflowId)}`,
    );
  }

  async revealSecret(
    workflowId: string,
    path: string,
  ): Promise<Record<string, string>> {
    return this.postJson<Record<string, string>>(
      `/workflows/${encodeURIComponent(workflowId)}/secrets/reveal`,
      { path },
    );
  }

  async getPollerStatus(): Promise<PollerStatusDto> {
    return this.getJson<PollerStatusDto>('/poller/status');
  }

  async getCapabilities(): Promise<CapabilitiesDto> {
    return this.getJson<CapabilitiesDto>('/capabilities');
  }

  async launchDeleteInfra(workflowId: string): Promise<DeleteInfraLaunchDto> {
    return this.postJson<DeleteInfraLaunchDto>(
      `/workflows/${encodeURIComponent(workflowId)}/delete-infra`,
      {},
    );
  }
}
